import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, DatePicker, toast, ExpandedTooltip } from '@livable/saas-react-components';
import { Button, FormControl, FormHelperText, Grid, InputAdornment, InputLabel, OutlinedInput, IconButton, Skeleton, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RangeDatePicker from 'src/components/general/RangeDatePicker';
import { track, events } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import useModalFooterStyles from 'src/styles/cutomModalFooter';
import { billSchema } from 'src/utils/formValidation/Allocations';
import { translate } from 'src/utils/translator';
import { AppfolioPushMessages, useAppfolioPush } from './AppfolioPush';
import { getBillTotalOverride } from './AppfolioPush/billTotalOverride';
import { BillAutomationExtractedLineItems, BillAutomationExtractedSummary, getBillAutomationErrorList, getBillAutomationWarningList, getExtractedInvoiceTotal, getExtractedTotalAmount, canDeleteRecoveredBill, getRecoveredBillDeleteNote, getRecoveredBillIntro, narrowExtractionToScope, ProcessCredentialLine } from './BillAutomationExtraction';
import ComparisonTable from './comparisonTable';
import Gwd from './gwd';
import useStyles from './styles';

/** The Bill total field before anyone touches it: it mirrors the extraction and sends no override. */
const UNEDITED_BILL_TOTAL = {
  value: '',
  isEdited: false,
  isEditable: false
};
const BillDetailsForm = observer(props => {
  var _utility$bill, _store$utilityProvide, _extractedBillData$pr, _utility$bill3, _utility$bill4, _utility$bill5, _utility$bill6, _utility$bill9, _utility$bill0, _store$allocations$cu2, _property$allocations8, _property$allocations0, _property$allocations10, _store$files$fileData, _store$homeDashboardS, _store$homeDashboardS2, _property$allocations11, _property$allocations12;
  const classes = useStyles();
  const modalFooterClasses = useModalFooterStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Apply Billing';
  const isBillExtractionEnabled = store.account.featureFlagsStore.getIsBillExtractionEnabled;
  /** The AP push UI is gated by the same environment flag the bill-automation service checks. */
  const isApPushEnabled = store.account.featureFlagsStore.getIsPushUtilityBillAppFolioEnabled && store.company.companyFeatureStore.isAppfolio;
  const billInfoAmountsRowMd = isBillExtractionEnabled ? 12 : 6;
  const billAutomationResponse = props.billAutomationResponse,
    handleClose = props.handleClose,
    selectedCycleStartDate = props.selectedCycleStartDate,
    showPreview = props.showPreview,
    _props$utility = props.utility,
    utility = _props$utility === void 0 ? {} : _props$utility,
    isSubmeterUtility = props.isSubmeterUtility,
    onSubmeterBillSaved = props.onSubmeterBillSaved;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showBillAutomationDetails = _useState2[0],
    setShowBillAutomationDetails = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isGwdSetting = _useState4[0],
    setIsGwdSetting = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    customChargeOnDate = _useState6[0],
    setCustomChargeOnDate = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    invoiceNumber = _useState8[0],
    setInvoiceNumber = _useState8[1];
  /**
   * Shown beside the invoice number, never folded into it. Read-only: nothing consumes it — the AP push
   * identifies a payable by the invoice number and explicitly refuses an account number in that role.
   */
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    accountNumber = _useState0[0],
    setAccountNumber = _useState0[1];
  const _useState1 = useState(UNEDITED_BILL_TOTAL),
    _useState10 = _slicedToArray(_useState1, 2),
    totalToAllocateAmount = _useState10[0],
    setTotalToAllocateAmount = _useState10[1];
  const billTotalDocumentIdRef = useRef(billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId);
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    postingDate = _useState12[0],
    setPostingDate = _useState12[1];
  const _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    billDueDate = _useState14[0],
    setBillDueDate = _useState14[1];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _useState15 = useState('Charge on date'),
    _useState16 = _slicedToArray(_useState15, 2),
    datePickerLabel = _useState16[0],
    setDatePickerLabel = _useState16[1];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    defaultChargeOnDateValue = _useState18[0],
    setDefaultChargeOnDateValue = _useState18[1];

  /**
   * A hand-edited Bill total belongs to one document. When the upload response names a different bill —
   * a new file replacing the last one — the edit goes with the old document instead of riding along as
   * the override for the new one.
   */
  useEffect(() => {
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    if (billTotalDocumentIdRef.current === billId) return;
    billTotalDocumentIdRef.current = billId;
    setTotalToAllocateAmount(UNEDITED_BILL_TOTAL);
  }, [billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId]);
  const comparison = utility.comparison || {};
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const billingTab = currentCompany.billingTab;
  const applyBill = currentCompany.billingTab.applyBill || {};
  const billingCycleId = applyBill.billingCycleId;
  const id = ((_utility$bill = utility.bill) === null || _utility$bill === void 0 ? void 0 : _utility$bill.id) || '';
  const property = store.homeDashboardStore.selectedPropertyId === 'all' ? store.homeDashboardStore.getCurrentCompanyProperties.find(p => p.id === applyBill.propertyId) || {} : store.homeDashboardStore.getCurrentProperty || {};
  const propertyId = property.id;
  const currentUtilityTypeName = (_store$utilityProvide = store.utilityProviders.utilities.get(applyBill.utilityId)) === null || _store$utilityProvide === void 0 ? void 0 : _store$utilityProvide.utilityTypeName;

  /**
   * Which service on the bill this screen is allocating. The property matters as much as the utility:
   * one bill can charge several properties for the same utility, each with its own subtotal, and the
   * utility alone would read whichever the provider printed first.
   */
  const extractionScope = useMemo(() => ({
    propertyId,
    utilityId: applyBill.utilityId,
    utilityTypeName: currentUtilityTypeName
  }), [propertyId, applyBill.utilityId, currentUtilityTypeName]);
  const setApplyBillAutosave = currentCompany.billingTab.setApplyBillAutosave;
  const appliedExtractedBillIdRef = useRef('');
  const lastSyncedUtilityBillIdRef = useRef(undefined);
  const billAutomationErrorList = useMemo(() => getBillAutomationErrorList(billAutomationResponse), [billAutomationResponse]);
  const billAutomationWarningList = useMemo(() => getBillAutomationWarningList(billAutomationResponse), [billAutomationResponse]);
  const hasBillAutomationErrors = billAutomationErrorList.length > 0;
  const hasBillAutomationWarnings = billAutomationWarningList.length > 0;

  // Bill details are only populated when validation succeeds AND no blocking errors are reported.
  const extractedBillData = (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status) === 'Validated' && billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.data && !hasBillAutomationErrors ? billAutomationResponse.data : null;
  /**
   * What the summary and the line items show: this property, this utility. The full payload stays
   * for the AP push and the count below, which are about the whole invoice, not this allocation.
   */
  const scopedBillData = useMemo(() => extractedBillData ? narrowExtractionToScope(extractedBillData, extractionScope) : null, [extractedBillData, extractionScope]);
  const totalExtractedServices = (extractedBillData === null || extractedBillData === void 0 || (_extractedBillData$pr = extractedBillData.properties) === null || _extractedBillData$pr === void 0 ? void 0 : _extractedBillData$pr.reduce((acc, p) => {
    var _p$services;
    return acc + (((_p$services = p.services) === null || _p$services === void 0 ? void 0 : _p$services.length) || 0);
  }, 0)) || 0;
  const additionalUtilitiesCount = Math.max(0, totalExtractedServices - 1);

  /**
   * The document id comes from the bill automation upload response; without it there is nothing to push.
   * Placed after `extractedBillData` because it reads it: the extraction says whether the bill already
   * has an invoice number worth trusting, and the field says what a person put there when it does not.
   */
  const appfolioPush = useAppfolioPush({
    billId: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId,
    billStatus: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status,
    extraction: extractedBillData,
    isEnabled: isApPushEnabled,
    typedInvoiceNumber: invoiceNumber
  });
  useEffect(() => {
    if (!extractedBillData) {
      setShowBillAutomationDetails(false);
    }
  }, [extractedBillData]);
  const getCAD = useCallback(() => {
    if (utility.commonAreaDeduction >= 0) return utility.commonAreaDeduction;
    if (applyBill.commonAreaDeduction >= 0) return applyBill.commonAreaDeduction;
    return 5;
  }, [utility.commonAreaDeduction, applyBill.commonAreaDeduction]);
  const getGoodWillDeduction = useCallback(() => {
    var _utility$bill2;
    if (applyBill.goodWillDeduction !== null && applyBill.goodWillDeduction >= 0) return applyBill.goodWillDeduction;
    if (((_utility$bill2 = utility.bill) === null || _utility$bill2 === void 0 ? void 0 : _utility$bill2.goodwillDeduction) >= 0) return utility.bill.goodwillDeduction;
    return 5;
  }, [applyBill.goodWillDeduction, (_utility$bill3 = utility.bill) === null || _utility$bill3 === void 0 ? void 0 : _utility$bill3.goodwillDeduction]);
  const billInitialValues = {
    amount: ((_utility$bill4 = utility.bill) === null || _utility$bill4 === void 0 ? void 0 : _utility$bill4.amount) || 0,
    commonAreaDeduction: getCAD(),
    date: {
      endDate: ((_utility$bill5 = utility.bill) === null || _utility$bill5 === void 0 ? void 0 : _utility$bill5.endDateOnly) || '',
      startDate: ((_utility$bill6 = utility.bill) === null || _utility$bill6 === void 0 ? void 0 : _utility$bill6.startDateOnly) || ''
    },
    goodWillDeduction: getGoodWillDeduction()
  };
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isReinitialize = _useState20[0],
    setIsReinitialize = _useState20[1];
  useEffect(() => {
    var _utility$bill7, _utility$bill8;
    if (!isSubmeterUtility) return;
    if ((_utility$bill7 = utility.bill) !== null && _utility$bill7 !== void 0 && _utility$bill7.startDateOnly && (_utility$bill8 = utility.bill) !== null && _utility$bill8 !== void 0 && _utility$bill8.endDateOnly) {
      setIsReinitialize(true);
    }
  }, [(_utility$bill9 = utility.bill) === null || _utility$bill9 === void 0 ? void 0 : _utility$bill9.startDateOnly, (_utility$bill0 = utility.bill) === null || _utility$bill0 === void 0 ? void 0 : _utility$bill0.endDateOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAllocationTable = async data => {
    const resp = await store.allocations.getAllocationTableAggregateAsync.run(data);
    return resp;
  };
  const postBillCreated = async data => {
    const resp = await store.allocations.postBillCreatedAsync.run(data);
    return resp;
  };
  const putBillUpdated = async data => {
    const resp = await store.allocations.putBillUpdatedAsync.run(data);
    return resp;
  };
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    updateBillChargeOnDateTrigger = _useState22[0],
    setUpdateBillChargeOnDateTrigger = _useState22[1];
  const updateBillChargeOnDate = async (billId, chargeOnDate) => {
    return store.appfolioBillsApi.postBillSettingsAsync.run({
      billId: billId,
      chargeOnDate: chargeOnDate,
      propertyId: propertyId
    }).then(postBillSettingsAsyncResp => {
      if (postBillSettingsAsyncResp !== null && postBillSettingsAsyncResp !== void 0 && postBillSettingsAsyncResp.isSuccessful) {
        if (customChargeOnDate === chargeOnDate) return;
        if (chargeOnDate === null) {
          toast('success', {
            body: [t('Bill charge on date cleared!')]
          });
        } else {
          toast('success', {
            body: [t('Bill charge on date updated!')]
          });
        }
      } else {
        toast('error', {
          body: [t('While updating bill charge on date!')]
        });
      }
    }).finally(() => setUpdateBillChargeOnDateTrigger(prev => !prev));
  };
  const getFileId = () => {
    var _property$allocations, _property$allocations2, _utility$bill1, _utility$bill10, _utility$bill11, _utility$bill12;
    return (_property$allocations = property.allocations) !== null && _property$allocations !== void 0 && (_property$allocations = _property$allocations.tempFileData) !== null && _property$allocations !== void 0 && _property$allocations.fileId ? (_property$allocations2 = property.allocations) === null || _property$allocations2 === void 0 || (_property$allocations2 = _property$allocations2.tempFileData) === null || _property$allocations2 === void 0 ? void 0 : _property$allocations2.fileId : utility !== null && utility !== void 0 && (_utility$bill1 = utility.bill) !== null && _utility$bill1 !== void 0 && _utility$bill1.redactedFileId ? utility === null || utility === void 0 || (_utility$bill10 = utility.bill) === null || _utility$bill10 === void 0 ? void 0 : _utility$bill10.redactedFileId : utility !== null && utility !== void 0 && (_utility$bill11 = utility.bill) !== null && _utility$bill11 !== void 0 && _utility$bill11.fileId ? utility === null || utility === void 0 || (_utility$bill12 = utility.bill) === null || _utility$bill12 === void 0 ? void 0 : _utility$bill12.fileId : '';
  };
  const fileAttachedAsync = async billIdId => {
    var _property$allocations3;
    let fileAttachedData = {};
    if ((_property$allocations3 = property.allocations) !== null && _property$allocations3 !== void 0 && (_property$allocations3 = _property$allocations3.tempFileData) !== null && _property$allocations3 !== void 0 && _property$allocations3.isDeleted) {
      fileAttachedData = {
        billId: billIdId,
        fileId: null,
        isShared: false
      };
    } else if (getFileId()) {
      var _property$allocations4, _utility$bill13;
      if ((_property$allocations4 = property.allocations) !== null && _property$allocations4 !== void 0 && (_property$allocations4 = _property$allocations4.tempFileData) !== null && _property$allocations4 !== void 0 && _property$allocations4.isReducted || utility !== null && utility !== void 0 && (_utility$bill13 = utility.bill) !== null && _utility$bill13 !== void 0 && _utility$bill13.redactedFileId) {
        var _utility$bill14, _utility$bill15, _property$allocations5;
        fileAttachedData = {
          billId: billIdId,
          fileId: (_utility$bill14 = utility.bill) !== null && _utility$bill14 !== void 0 && _utility$bill14.fileId ? (_utility$bill15 = utility.bill) === null || _utility$bill15 === void 0 ? void 0 : _utility$bill15.fileId : getFileId() ? getFileId() : '',
          redactedFileId: getFileId(),
          isShared: (_property$allocations5 = property.allocations) === null || _property$allocations5 === void 0 || (_property$allocations5 = _property$allocations5.tempFileData) === null || _property$allocations5 === void 0 ? void 0 : _property$allocations5.isShared
        };
      } else {
        var _property$allocations6;
        fileAttachedData = {
          billId: billIdId,
          fileId: getFileId(),
          isShared: (_property$allocations6 = property.allocations) === null || _property$allocations6 === void 0 || (_property$allocations6 = _property$allocations6.tempFileData) === null || _property$allocations6 === void 0 ? void 0 : _property$allocations6.isShared
        };
      }
    }
    if (fileAttachedData.billId) await store.allocations.getFileAttachedAsync.run(fileAttachedData);
  };
  useEffect(() => {
    const billId = id;
    if (store.company.companyFeatureStore.isAppfolio) {
      if (!!billId && billId !== '') {
        const data = {
          billId,
          allocationCycleMonth: moment(applyBill.cycleName, 'MMM YYYY').month() + 1,
          allocationCycleYear: moment(applyBill.cycleName, 'MMM YYYY').year()
        };
        store.appfolioBillsApi.getSettingsByBillIdV2Async.run(data).then(getSettingsByBillIdAsyncResp => {
          if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.isSuccessful) {
            if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.response) {
              const _getSettingsByBillIdA = getSettingsByBillIdAsyncResp.response,
                chargeOnDate = _getSettingsByBillIdA.chargeOnDate,
                defaultChargeOnDate = _getSettingsByBillIdA.defaultChargeOnDate;
              setCustomChargeOnDate(chargeOnDate);
              if (defaultChargeOnDate) {
                setDatePickerLabel("Charge on date will be set to ".concat(moment(defaultChargeOnDate).format('MM/DD/YYYY')));
                setDefaultChargeOnDateValue(defaultChargeOnDate);
              }
            }
          }
        });
      } else if (propertyId && applyBill.cycleName) {
        // If billId is not provided, we fetch the default charge on date based on property
        const data = {
          propertyId,
          allocationCycleMonth: moment(applyBill.cycleName, 'MMM YYYY').month() + 1,
          allocationCycleYear: moment(applyBill.cycleName, 'MMM YYYY').year()
        };
        store.appfolioPropertiesApi.getDefaultChargeOnDateAsync.run(data).then(getSettingsByBillIdAsyncResp => {
          if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.isSuccessful) {
            setDefaultChargeOnDateValue(getSettingsByBillIdAsyncResp.response);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, updateBillChargeOnDateTrigger]);
  useEffect(() => {
    var _property$allocations7, _utility$bill$id, _utility$bill16, _utility$bill17, _utility$bill18, _utility$bill19, _store$allocations$cu;
    if ((_property$allocations7 = property.allocations) !== null && _property$allocations7 !== void 0 && (_property$allocations7 = _property$allocations7.tempFileData) !== null && _property$allocations7 !== void 0 && _property$allocations7.isDeleted) return;
    const currentBillId = (_utility$bill$id = utility === null || utility === void 0 || (_utility$bill16 = utility.bill) === null || _utility$bill16 === void 0 ? void 0 : _utility$bill16.id) !== null && _utility$bill$id !== void 0 ? _utility$bill$id : '';
    const billIdentityChanged = lastSyncedUtilityBillIdRef.current !== currentBillId;
    if (billIdentityChanged) {
      lastSyncedUtilityBillIdRef.current = currentBillId;
    }

    // After extraction, don't clobber invoice/posting/due when utility is refetched (same bill row)
    if (extractedBillData && !billIdentityChanged) return;
    const serverInvoice = (utility === null || utility === void 0 || (_utility$bill17 = utility.bill) === null || _utility$bill17 === void 0 ? void 0 : _utility$bill17.invoiceNumber) || '';
    const serverPosting = utility !== null && utility !== void 0 && (_utility$bill18 = utility.bill) !== null && _utility$bill18 !== void 0 && _utility$bill18.postingDateOnly ? moment(utility.bill.postingDateOnly).toDate() : null;
    const serverDue = utility !== null && utility !== void 0 && (_utility$bill19 = utility.bill) !== null && _utility$bill19 !== void 0 && _utility$bill19.dueDateOnly ? moment(utility.bill.dueDateOnly).toDate() : null;
    const cycleDue = (_store$allocations$cu = store.allocations.currentCycle) !== null && _store$allocations$cu !== void 0 && _store$allocations$cu.dueDate ? moment(store.allocations.currentCycle.dueDate).toDate() : null;
    if (billIdentityChanged) {
      setInvoiceNumber(serverInvoice);
      // The allocation bill carries no account number, so a new bill clears it rather than inheriting
      // the last one's — an account number belongs to one bill and to nothing else.
      setAccountNumber('');
      setPostingDate(serverPosting);
      setBillDueDate(serverDue || cycleDue || null);
    } else {
      // Same bill after refetch: only overwrite when server sends a value (preserve local edits)
      if (serverInvoice) setInvoiceNumber(serverInvoice);
      if (serverPosting) setPostingDate(serverPosting);
      if (serverDue) setBillDueDate(serverDue);
    }
  }, [(_store$allocations$cu2 = store.allocations.currentCycle) === null || _store$allocations$cu2 === void 0 ? void 0 : _store$allocations$cu2.dueDate, utility, extractedBillData, (_property$allocations8 = property.allocations) === null || _property$allocations8 === void 0 || (_property$allocations8 = _property$allocations8.tempFileData) === null || _property$allocations8 === void 0 ? void 0 : _property$allocations8.isDeleted]);
  const formik = useFormik({
    initialValues: billInitialValues,
    enableReinitialize: !!isSubmeterUtility,
    onSubmit: async values => {
      track(events.clicked('Calculate Allocation'), {
        page,
        isSubmeterUtility: !!isSubmeterUtility,
        isExtracted: !!extractedBillData,
        utility: applyBill.utilityName
      });
      currentCompany.billingTab.setIsModalLoading(true);
      setApplyBillAutosave(true);
      const data = {
        amount: parseFloat(values.amount),
        endDateOnly: moment(values.date.endDate).format(moment.HTML5_FMT.DATE),
        goodWillDeduction: parseFloat(values.goodWillDeduction),
        isExcluded: true,
        startDateOnly: moment(values.date.startDate).format(moment.HTML5_FMT.DATE)
      };
      if (isSubmeterUtility) {
        const billPayload = id ? putBillUpdated(_objectSpread(_objectSpread({}, data), {}, {
          id
        })) : postBillCreated(_objectSpread(_objectSpread({}, data), {}, {
          billingCycleId,
          propertyId,
          utilityId: applyBill.utilityId
        }));
        billPayload.then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          const billId = id || (resp === null || resp === void 0 ? void 0 : resp.response);
          return Promise.all([fileAttachedAsync(billId), updateBillChargeOnDate(billId, customChargeOnDate)]).then(() => getAllocationTable({
            billingCycleId,
            propertyId,
            utilityId: applyBill.utilityId
          })).then(() => {
            currentCompany.billingTab.setIsModalLoading(false);
            onSubmeterBillSaved === null || onSubmeterBillSaved === void 0 || onSubmeterBillSaved();
          });
        });
      } else if (id) {
        putBillUpdated(_objectSpread(_objectSpread({}, data), {}, {
          id
        })).then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          if (resp !== null && resp !== void 0 && resp.isSuccessful) {
            return Promise.all([fileAttachedAsync(id), getAllocationTable({
              billingCycleId,
              propertyId,
              utilityId: applyBill.utilityId
            })]).then(() => {
              setApplyBillAutosave(true);
              setTimeout(() => setApplyBillAutosave(false), 2000);
              currentCompany.billingTab.setIsBillEdit(false);
            });
          }
        });
      } else {
        postBillCreated(_objectSpread(_objectSpread({}, data), {}, {
          billingCycleId,
          propertyId,
          utilityId: applyBill.utilityId
        })).then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          if (resp !== null && resp !== void 0 && resp.isSuccessful) {
            return Promise.all([fileAttachedAsync(resp === null || resp === void 0 ? void 0 : resp.response), updateBillChargeOnDate(resp === null || resp === void 0 ? void 0 : resp.response, customChargeOnDate), getAllocationTable({
              billingCycleId,
              propertyId,
              utilityId: applyBill.utilityId
            })]).then(() => {
              setApplyBillAutosave(true);
              setTimeout(() => setApplyBillAutosave(false), 2000);
              currentCompany.billingTab.setIsBillEdit(false);
            });
          }
        });
      }
    },
    validationSchema: billSchema
  });
  const formikRef = useRef(null);
  formikRef.current = formik;

  /**
   * Puts the bill fields back to empty, along with everything derived from the extraction that filled
   * them. Shared, so removing a bill leaves the same state however it was removed.
   */
  const clearExtractedBillFields = useCallback(() => {
    setShowBillAutomationDetails(false);
    setIsGwdSetting(false);
    setInvoiceNumber('');
    setAccountNumber('');
    setPostingDate(null);
    setBillDueDate(null);
    setCustomChargeOnDate(null);
    setDatePickerLabel('Charge on date');
    setDefaultChargeOnDateValue(null);
    setTotalToAllocateAmount(UNEDITED_BILL_TOTAL);
    appliedExtractedBillIdRef.current = '';
    const f = formikRef.current;
    if (f) {
      f.resetForm({
        values: {
          amount: 0,
          commonAreaDeduction: getCAD(),
          date: {
            endDate: '',
            startDate: ''
          },
          goodWillDeduction: getGoodWillDeduction()
        }
      });
    }
    setIsReinitialize(true);
  }, [getCAD, getGoodWillDeduction]);
  useEffect(() => {
    var _property$allocations9;
    if (!((_property$allocations9 = property.allocations) !== null && _property$allocations9 !== void 0 && (_property$allocations9 = _property$allocations9.tempFileData) !== null && _property$allocations9 !== void 0 && _property$allocations9.isDeleted)) return;
    clearExtractedBillFields();
  }, [(_property$allocations0 = property.allocations) === null || _property$allocations0 === void 0 || (_property$allocations0 = _property$allocations0.tempFileData) === null || _property$allocations0 === void 0 ? void 0 : _property$allocations0.isDeleted, clearExtractedBillFields]);

  /**
   * The bill these fields were filled from is gone — deleted, or replaced by a newly attached file.
   *
   * Only when an extraction had actually been applied, which the ref records: a person who typed the
   * fields in themselves has nothing here to clear, and clearing anyway would throw away their work.
   */
  useEffect(() => {
    if (billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId) return;
    if (!appliedExtractedBillIdRef.current) return;
    clearExtractedBillFields();
  }, [billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, clearExtractedBillFields]);
  useEffect(() => {
    var _property$allocations1;
    if ((_property$allocations1 = property.allocations) !== null && _property$allocations1 !== void 0 && (_property$allocations1 = _property$allocations1.tempFileData) !== null && _property$allocations1 !== void 0 && _property$allocations1.isDeleted) return;
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    // Only apply when extraction succeeded without blocking errors.
    if (!extractedBillData || !billId) return;
    if (appliedExtractedBillIdRef.current === billId) return;
    const data = extractedBillData;
    const getVal = keys => {
      const key = keys.find(k => data[k] !== undefined && data[k] !== null);
      return key ? data[key] : null;
    };
    const getDate = keys => {
      const v = getVal(keys);
      if (!v) return null;
      const m = moment(v);
      return m.isValid() ? m : null;
    };

    // Invoice keys only. `accountNumber` used to lead this list, so it won whenever extraction found one
    // — displacing a real invoice number that sat right beside it, and showing as "Invoice number" the
    // one value the AP push explicitly refuses in that role (it repeats on every statement a biller
    // sends). The two are separate fields now, and neither ever fills from the other.
    const extractedInvoiceNumber = getVal(['invoiceNumber', 'invoiceNo', 'invoice']);
    const extractedAccountNumber = getVal(['accountNumber']);
    const extractedPostingDate = getDate(['billDate', 'postingDate', 'postingDateOnly']);
    const extractedDueDate = getDate(['dueDate', 'dueDateOnly', 'billDueDate']);
    const extractedStartDate = getDate(['serviceStartDate', 'serviceStartDateOnly', 'startDateOnly']);
    const extractedEndDate = getDate(['serviceEndDate', 'serviceEndDateOnly', 'endDateOnly']);
    const extractedAmount = getExtractedTotalAmount(data, extractionScope);
    if (extractedInvoiceNumber != null) setInvoiceNumber(String(extractedInvoiceNumber));
    if (extractedAccountNumber != null) setAccountNumber(String(extractedAccountNumber));
    if (extractedPostingDate) setPostingDate(extractedPostingDate.toDate());
    if (extractedDueDate) setBillDueDate(extractedDueDate.toDate());
    const formikInstance = formikRef.current;
    if (formikInstance) {
      if (extractedStartDate || extractedEndDate) {
        var _formikInstance$value, _formikInstance$value2;
        formikInstance.setFieldValue('date', {
          startDate: extractedStartDate ? extractedStartDate.format(moment.HTML5_FMT.DATE) : ((_formikInstance$value = formikInstance.values.date) === null || _formikInstance$value === void 0 ? void 0 : _formikInstance$value.startDate) || '',
          endDate: extractedEndDate ? extractedEndDate.format(moment.HTML5_FMT.DATE) : ((_formikInstance$value2 = formikInstance.values.date) === null || _formikInstance$value2 === void 0 ? void 0 : _formikInstance$value2.endDate) || ''
        });
        // RangeDatePicker keeps internal state; it only syncs from props when reinitialize is true
        setIsReinitialize(true);
      }
      if (extractedAmount !== null) {
        formikInstance.setFieldValue('amount', extractedAmount);
      }
    }
    appliedExtractedBillIdRef.current = billId;
  }, [extractionScope, billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, extractedBillData, (_property$allocations10 = property.allocations) === null || _property$allocations10 === void 0 || (_property$allocations10 = _property$allocations10.tempFileData) === null || _property$allocations10 === void 0 ? void 0 : _property$allocations10.isDeleted]);
  const isCycleFromPreviousMonth = () => {
    if (!formik.values.date.startDate) {
      const today = moment().toDate();
      const cycleStartDate = moment(selectedCycleStartDate).toDate();
      return moment(cycleStartDate).isBefore(today, 'month');
    }
  };
  const getComparisonBlock = () => {
    if (showPreview) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotal
      }, "Allocation total: ", /*#__PURE__*/React.createElement("b", null, "$", utility.allocated.toFixed(2)), comparison !== null && comparison !== void 0 && comparison.isBillIncreasedBy20Percent ? /*#__PURE__*/React.createElement(Icon, {
        state: "warning",
        type: "arrow-down"
      }) : null), /*#__PURE__*/React.createElement("div", {
        className: classes.sectionTitle
      }, "Allocation history"), comparison !== null && comparison !== void 0 && comparison.isBillIncreasedBy20Percent ? /*#__PURE__*/React.createElement("div", {
        className: classes.allocationWarning
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationWarningContent
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "warning",
        state: "warning"
      }), /*#__PURE__*/React.createElement("span", null, "Your allocation amount has increased by more than 20% since last time. "))) : null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTable
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTableContent
      }, /*#__PURE__*/React.createElement(ComparisonTable, {
        comparison: comparison
      }))));
    } else if (applyBill.status === 'PendingCompletion') {
      const comparisonWithoutUtility = property === null || property === void 0 ? void 0 : property.allocations.comparisonById.get(billingCycleId + applyBill.utilityId);
      if ((comparisonWithoutUtility === null || comparisonWithoutUtility === void 0 ? void 0 : comparisonWithoutUtility.allocations.length) === 0) return null;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotal
      }), /*#__PURE__*/React.createElement("div", {
        className: classes.sectionTitle
      }, "Allocation history"), /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTable
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTableContent
      }, /*#__PURE__*/React.createElement(ComparisonTable, {
        comparison: comparisonWithoutUtility
      }))));
    }
  };
  const getLabel = () => {
    if (customChargeOnDate) return 'Charge on date';else if (defaultChargeOnDateValue) return moment(defaultChargeOnDateValue).format('MM/DD/YYYY');
    return 'Charge on date';
  };
  const handleSkipBill = () => {
    var _property$buildingInf, _property$dashboard;
    track(events.clicked('Skip Bill'), {
      page,
      utility: applyBill.utilityName
    });
    billingTab.setSkipModal({
      allocationCycleId: applyBill.allocationCycleId,
      billingCycleId,
      buildingAddress: applyBill.buildingAddress || ((_property$buildingInf = property.buildingInformation) === null || _property$buildingInf === void 0 ? void 0 : _property$buildingInf.buildingAddress) || ((_property$dashboard = property.dashboard) === null || _property$dashboard === void 0 ? void 0 : _property$dashboard.propertyAddress),
      cycleName: applyBill.cycleName,
      isEdit: false,
      propertyId,
      utilityId: applyBill.utilityId,
      utilityName: applyBill.utilityName
    });
    billingTab.setSkipModalOpen(true);
    handleClose();
  };

  /**
   * What the provider is owed for the whole bill file, which is not the same as what is being
   * allocated: the amount above is narrowed to the one utility this table charges for. Read from
   * the extraction so a bill covering several utilities shows the full invoice here, not the slice.
   *
   * A manual edit still wins, and with nothing extracted it mirrors the amount as it used to, so a
   * bill whose extraction carries no `invoiceTotals` behaves exactly as before.
   */
  const extractedInvoiceTotal = useMemo(() => getExtractedInvoiceTotal(extractedBillData), [extractedBillData]);
  const totalToAllocateAmountValue = totalToAllocateAmount.isEdited ? totalToAllocateAmount.value : extractedInvoiceTotal !== null && extractedInvoiceTotal !== void 0 ? extractedInvoiceTotal : formik.values.amount;
  const handleEditTotalToAllocateAmount = () => {
    track(events.clicked('Edit Total To Allocate Amount'), {
      page,
      utility: applyBill.utilityName
    });
    setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
      value: totalToAllocateAmountValue,
      isEditable: true
    }));
  };
  const handleSaveTotalToAllocateAmount = () => {
    track(events.clicked('Save Total To Allocate Amount'), {
      page,
      utility: applyBill.utilityName
    });
    setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isEditable: false
    }));
  };

  /**
   * A hand-edited Bill total overrides what the push sends to AppFolio — it used to be display-only, with
   * the Edit control saving nowhere. Only a SAVED edit is sent (the button is disabled while one is
   * open); an untouched or cleared field sends nothing, and the push reads what OCR extracted for itself.
   */
  const handlePushApToAppFolio = () => {
    track(events.clicked('Push AP to AppFolio'), {
      page,
      utility: applyBill.utilityName
    });
    appfolioPush.handlePush(getBillTotalOverride(totalToAllocateAmount));
  };
  const extractedBillFileId = getFileId();
  // A recovered bill was never uploaded through the file service in this session, so there is no
  // fileDataById entry to read a name from — the bill carries its own, and it is the only one there is.
  const extractedBillFileName = extractedBillFileId ? ((_store$files$fileData = store.files.fileDataById.get(extractedBillFileId)) === null || _store$files$fileData === void 0 ? void 0 : _store$files$fileData.fileName) || '' : (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.fileName) || '';
  const billAutomationCustomerName = ((_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.companyLegalName) || ((_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.name) || '';
  const automationStatus = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status;
  /**
   * Whether there is a bill behind this screen at all — the gate on every extraction banner below.
   *
   * A file-service id is no longer the only way to have one. A bill recovered for this cycle was
   * uploaded in an earlier session and its document lives in bill-automation, so it has no
   * file-service id here; the extraction it carries is just as real, and this panel is the only place
   * a person can read it.
   */
  const hasBillToShow = (!!getFileId() || !!(billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId)) && !((_property$allocations11 = property.allocations) !== null && _property$allocations11 !== void 0 && (_property$allocations11 = _property$allocations11.tempFileData) !== null && _property$allocations11 !== void 0 && _property$allocations11.isDeleted);
  const showBillAutomationLoadingBanner = hasBillToShow && billAutomationResponse && automationStatus !== 'Validated' && automationStatus !== 'Failed';
  const showBillAutomationErrorBanner = hasBillToShow && !showBillAutomationLoadingBanner && (automationStatus === 'Failed' || hasBillAutomationErrors);
  const showBillAutomationSuccessBanner = hasBillToShow && !showBillAutomationLoadingBanner && !showBillAutomationErrorBanner && !!extractedBillData;
  /**
   * Only for a bill brought back for this cycle, and only while it is still there to act on. Saying
   * "you left this unfinished" about a file the person uploaded a moment ago would be nonsense.
   */
  const isRecoveredBill = hasBillToShow && !!((_property$allocations12 = property.allocations) !== null && _property$allocations12 !== void 0 && (_property$allocations12 = _property$allocations12.tempFileData) !== null && _property$allocations12 !== void 0 && _property$allocations12.wasRecovered) && !!(billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId);
  const _useState23 = useState(''),
    _useState24 = _slicedToArray(_useState23, 2),
    dismissedBannerBillId = _useState24[0],
    setDismissedBannerBillId = _useState24[1];
  // Tied to the bill it was dismissed for, so a different one still gets to introduce itself. Today
  // the modal remounts on every cycle, property and utility change, so nothing else could reach this
  // component with a second recovered bill — but that is a key in another file, not a guarantee here.
  const showRecoveredBanner = isRecoveredBill && dismissedBannerBillId !== (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId);
  const recoveredIntro = getRecoveredBillIntro(billAutomationResponse);
  const mayDeleteRecoveredBill = isRecoveredBill && canDeleteRecoveredBill(billAutomationResponse, applyBill.utilityId);
  const showBillAutomationWarningBanner = hasBillToShow && !showBillAutomationLoadingBanner && hasBillAutomationWarnings;
  const extractedDisplayValues = useMemo(() => {
    if (!extractedBillData) return {
      serviceDatesText: '',
      amountText: ''
    };
    const data = extractedBillData;
    const pickValue = keys => {
      const key = keys.find(k => data[k] !== undefined && data[k] !== null);
      return key ? data[key] : null;
    };
    const pickDateDisplay = keys => {
      const value = pickValue(keys);
      if (!value) return '-';
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('MM/DD/YY') : '-';
    };
    const startText = pickDateDisplay(['serviceStartDate', 'serviceStartDateOnly', 'startDateOnly']);
    const endText = pickDateDisplay(['serviceEndDate', 'serviceEndDateOnly', 'endDateOnly']);
    const amount = getExtractedTotalAmount(data, extractionScope);
    return {
      serviceDatesText: "".concat(startText, " - ").concat(endText),
      amountText: amount !== null ? "$ ".concat(amount) : ''
    };
  }, [extractedBillData, extractionScope]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.billDetailsWrapper
  }, /*#__PURE__*/React.createElement("form", {
    "data-tut": "reactour__allocation-form"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.serviceDate
  }, showRecoveredBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.recoveredBanner),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.recoveredBannerIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "info"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, recoveredIntro.title), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, recoveredIntro.body), /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredBannerNote
  }, getRecoveredBillDeleteNote(mayDeleteRecoveredBill)))), /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Dismiss",
    className: classes.recoveredBannerDismiss,
    onClick: () => setDismissedBannerBillId((billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId) || ''),
    variant: "actionIcon"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  }))) : null, showBillAutomationLoadingBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerLoadingOnly),
    role: "status",
    "aria-live": "polite",
    "aria-busy": "true"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerSpinnerWrap,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle-animated"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, "Processing your bill"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, "Please wait while we extract and validate the information from your uploaded file. This may take a moment."))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.extractedBannerStopButton,
    onClick: () => {
      var _property$allocations13;
      track(events.clicked('Stop Bill Extraction'), {
        page,
        status: automationStatus
      });
      (_property$allocations13 = property.allocations) === null || _property$allocations13 === void 0 || _property$allocations13.setTempFileData('billAutomationResponse', null);
    },
    'custom-attribute': 'bill-automation-stop'
  }, t('Stop'))) : showBillAutomationErrorBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerError, " ").concat(classes.extractedBannerLoadingOnly),
    role: "alert"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-error"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorTitle
  }, "Extraction and validation could not be completed"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, "We were unable to process this bill for the following reason(s):"), billAutomationErrorList.length > 0 ? /*#__PURE__*/React.createElement("ul", {
    className: classes.extractedBannerErrorList
  }, billAutomationErrorList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: classes.extractedBannerErrorListItem
  }, msg))) : /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, "No detailed message was returned. Please confirm the file and property setup, then try again or contact support if the issue continues.")))) : showBillAutomationSuccessBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBanner
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerSparkleIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, "AI extracted this bill"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, "We have completed the fields below for you", additionalUtilitiesCount > 0 ? " and identified ".concat(additionalUtilitiesCount, " additional utilities across your properties in this bill.") : '.'))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    className: classes.extractedBannerButton,
    onClick: () => {
      track(events.clicked(showBillAutomationDetails ? 'Hide Extracted Bill Details' : 'View Extracted Bill Details'), {
        page
      });
      setShowBillAutomationDetails(v => !v);
    },
    'custom-attribute': 'bill-automation-view-details-toggle'
  }, showBillAutomationDetails ? 'Hide details' : 'View details')) : null, showBillAutomationWarningBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerWarning, " ").concat(classes.extractedBannerLoadingOnly),
    role: "status",
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-warning"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerWarningTitle
  }, "Review the following warnings"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerWarningIntro
  }, "We detected a few items that may need your attention:"), /*#__PURE__*/React.createElement("ul", {
    className: classes.extractedBannerWarningList
  }, billAutomationWarningList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: classes.extractedBannerWarningListItem
  }, msg)))))) : null, /*#__PURE__*/React.createElement(ProcessCredentialLine, {
    propertyUtilityId: applyBill.propertyUtilityId
  }), extractedBillData && showBillAutomationDetails ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BillAutomationExtractedSummary, {
    customerName: billAutomationCustomerName,
    data: scopedBillData,
    fileName: extractedBillFileName,
    scope: extractionScope,
    status: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status
  }), /*#__PURE__*/React.createElement(BillAutomationExtractedLineItems, {
    data: scopedBillData
  })) : null), /*#__PURE__*/React.createElement(Grid, {
    alignItems: "flex-start",
    columnSpacing: isBillExtractionEnabled ? 12 : 2,
    container: true,
    rowSpacing: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    md: billInfoAmountsRowMd,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionTitle
  }, "Bill info"), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.serviceDate).concat(!isBillExtractionEnabled ? " ".concat(classes.serviceDateInSplitRow) : '')
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12
  }, isBillExtractionEnabled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "accountNumber"
  }, "Account number"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    id: "accountNumber",
    name: "accountNumber",
    label: "Account number"
    // Read-only rather than disabled: an account number is worth copying, and a
    // disabled input is awkward to select from. Nothing reads what is typed here.
    ,
    readOnly: true,
    value: accountNumber
  }))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "invoiceNumber"
  }, "Invoice number"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    id: "invoiceNumber",
    name: "invoiceNumber",
    label: "Invoice number"
    // Locked when the extraction read one and parsed the document confidently:
    // the push uses that number and would ignore anything typed over it, so an
    // editable field here would only invite an edit that goes nowhere. Below the
    // confidence bar it opens, because then a person outranks the extractor.
    ,
    readOnly: appfolioPush.isInvoiceNumberFixed,
    value: invoiceNumber,
    onBlur: e => {
      if (e.target.value) track(events.filled('Invoice Number'), {
        page
      });
    },
    onChange: e => setInvoiceNumber(e.target.value)
  }))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "Posting date",
    onChange: date => {
      track(events.datePicked('Posting'), {
        page
      });
      setPostingDate(date);
    },
    value: postingDate
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dueDatePickerWrapper
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "Bill's due date",
    onChange: date => {
      track(events.datePicked('Bill Due'), {
        page
      });
      setBillDueDate(date);
    },
    value: billDueDate
  }), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('This due date is used when resident bill is generated.'),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.dueDateInfoIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "question"
  })))))) : null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.serviceDateDatePicked)
  }, /*#__PURE__*/React.createElement(RangeDatePicker, {
    fromDate: formik.values.date.startDate ? moment(formik.values.date.startDate).toDate() : '',
    toDate: formik.values.date.endDate ? moment(formik.values.date.endDate).toDate() : '',
    format: 'MMMM yyyy',
    handleDateChange: dates => {
      track(events.datePicked('Service Dates'), {
        page
      });
      formik.setFieldTouched(formik.values.date);
      formik.handleChange({
        target: {
          id: 'date',
          value: dates
        }
      });
    },
    id: "date",
    label: "Service dates",
    maxDate: moment(store.allocations.currentCycle.endDateOnly).add(store.company.companyFeatureStore.isAppfolio ? 15 : 1, 'days').toDate(),
    reinitialize: isReinitialize,
    setIsReinitialize: setIsReinitialize,
    showPreviousMonths: isCycleFromPreviousMonth()
  })), extractedBillData ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.extractedValueHint,
    variant: "body2"
  }, "Extracted: ", extractedDisplayValues.serviceDatesText) : null)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    md: billInfoAmountsRowMd,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.sectionTitle).concat(!isBillExtractionEnabled ? " ".concat(classes.sectionTitleAmountsSplitAlign) : '')
  }, "Amounts"), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.deductionsBox).concat(!isBillExtractionEnabled ? " ".concat(classes.deductionsBoxInSplitRow) : '')
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "amount"
  }, "Amount to allocate"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    id: "amount",
    name: "amount",
    label: "Amount to allocate",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      placement: "top",
      title: t('Amount prior to deductions. Typically excludes late fees or other line items ' + 'not passed through to residents, and may be less than the Bill total, ' + 'especially when there are multiple allocation tables for a single bill file.'),
      type: "grey"
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.amountInfoIcon
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "info",
      type: "question"
    })))),
    onBlur: e => {
      if (e.target.value !== '') track(events.filled('Amount to allocate'), {
        page
      });
    },
    type: "text",
    value: formik.values.amount
  }), formik.touched.amount && !!formik.errors.amount && /*#__PURE__*/React.createElement(FormHelperText, null, translate(formik.errors.amount, t))), extractedBillData && extractedDisplayValues.amountText ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.extractedValueHint,
    variant: "body2"
  }, "Extracted: ", extractedDisplayValues.amountText) : null)), isApPushEnabled ? /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12,
    className: classes.totalToAllocateAmountRow
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "totalToAllocateAmount"
  }, "Bill total"), /*#__PURE__*/React.createElement(OutlinedInput, {
    disabled: !totalToAllocateAmount.isEditable,
    fullWidth: true,
    id: "totalToAllocateAmount",
    name: "totalToAllocateAmount",
    label: "Bill total",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      placement: "top",
      title: t('(AP Push) The amount to be paid to the provider. This may be more than the ' + 'amount being allocated to residents, especially when the bill has more than ' + 'one utility and there are separate allocation tables for the same bill file.'),
      type: "grey"
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.amountInfoIcon
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "info",
      type: "question"
    }))), totalToAllocateAmount.isEditable ? /*#__PURE__*/React.createElement(Button, {
      className: classes.totalToAllocateAmountEditButton,
      variant: "actionButtonInvert",
      onClick: handleSaveTotalToAllocateAmount,
      'custom-attribute': '613-new-billing-module'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "check"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Save")) : /*#__PURE__*/React.createElement(Button, {
      className: classes.totalToAllocateAmountEditButton,
      variant: "actionButtonInvert",
      onClick: handleEditTotalToAllocateAmount,
      'custom-attribute': '612-new-billing-module'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Edit"))),
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
        value: e.target.value,
        isEdited: true
      }));
    },
    type: "text",
    value: totalToAllocateAmountValue
  })))) : null, appfolioPush.isVisible ? /*#__PURE__*/React.createElement(AppfolioPushMessages, {
    blockers: appfolioPush.blockers,
    disabledReason: appfolioPush.disabledReason,
    liveAttempt: appfolioPush.liveAttempt,
    pushStatus: appfolioPush.pushStatus,
    refusalCode: appfolioPush.refusalCode
  }) : null, store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 2,
    rowSpacing: 2
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.customChargeOnDateDatePicker)
  }, store.appfolioBillsApi.postBillSettingsAsync.inProgress || store.appfolioBillsApi.getSettingsByBillIdV2Async.inProgress || store.appfolioPropertiesApi.getDefaultChargeOnDateAsync.inProgress ? /*#__PURE__*/React.createElement(Skeleton, {
    variant: "rectangular",
    animation: "wave",
    height: 48,
    width: 220
  }) : /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: 'top',
    title: customChargeOnDate ? '' : datePickerLabel,
    type: 'success-bold-dark'
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: getLabel(),
    onChange: date => {
      const billId = id;
      const formatedDate = date === null || date === '' ? null : moment(date).format('YYYY-MM-DD');
      track(events.datePicked('Charge On'), {
        page
      });
      setCustomChargeOnDate(formatedDate);
      if (!!billId && billId !== '') updateBillChargeOnDate(billId, formatedDate);
    },
    value: customChargeOnDate !== null ? moment(customChargeOnDate).toDate() : null
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    style: {
      width: '48px',
      height: '48px',
      marginLeft: '6px'
    },
    disabled: customChargeOnDate === null || customChargeOnDate === undefined,
    onClick: () => {
      const billId = id;
      track(events.clicked('Clear Charge On Date'), {
        page
      });
      setCustomChargeOnDate(null);
      if (!!billId && billId !== '') {
        updateBillChargeOnDate(billId, null);
      }
    },
    'custom-attribute': '858-property-utilities-setup-page'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t("Set the date to post the charge or credit — if you leave it blank, we’ll use date from property settings. If it's not set - we’ll use today’s date."),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, null, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "question"
  }))))))))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionTitle
  }, "Deductions"), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionsBox
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItem
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemData
  }, "Common Area Deduction(CAD): ", /*#__PURE__*/React.createElement("b", null, billInitialValues.commonAreaDeduction, "%")), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemEdit
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: true,
    'custom-attribute': '606-new-billing-module'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "lock-locked"
  }), /*#__PURE__*/React.createElement("span", {
    className: "actionText"
  }, "Edit")))), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItem
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemData
  }, "Good Will Deduction (GWD): ", /*#__PURE__*/React.createElement("b", null, billInitialValues.goodWillDeduction, "%")), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemEdit
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => {
      track(events.clicked('Edit GWD'), {
        page,
        utility: applyBill.utilityName
      });
      setIsGwdSetting(true);
    },
    'custom-attribute': '607-new-billing-module'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  }), /*#__PURE__*/React.createElement("span", {
    className: "actionText"
  }, "Edit"))), isGwdSetting ? /*#__PURE__*/React.createElement(Gwd, {
    cadValue: billInitialValues.commonAreaDeduction,
    formik: formik,
    initialGWD: billInitialValues.goodWillDeduction,
    handleClose: () => setIsGwdSetting(false),
    propertyId: propertyId
  }) : null))), getComparisonBlock()), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.modalFooterStandartSmall
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerEven
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      track(events.clicked('Close'), {
        page,
        utility: applyBill.utilityName
      });
      handleClose();
    },
    variant: "text",
    'custom-attribute': '608-new-billing-module'
  }, t('Close'))), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerButton
  }, appfolioPush.isVisible ? /*#__PURE__*/React.createElement(Button, {
    className: classes.pushApButton,
    disabled: !appfolioPush.canPush || totalToAllocateAmount.isEditable,
    onClick: handlePushApToAppFolio,
    variant: "text",
    'custom-attribute': '611-new-billing-module'
  }, t('Push AP to AppFolio'), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('Push this bill to AppFolio without calculating an allocation.'),
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.pushApInfoIcon,
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "info"
  })))) : null, /*#__PURE__*/React.createElement(Button, {
    onClick: handleSkipBill,
    variant: "text",
    'custom-attribute': '610-new-billing-module'
  }, "Skip bill"), /*#__PURE__*/React.createElement(Button, {
    onClick: formik.handleSubmit,
    type: "submit",
    variant: "contained",
    'custom-attribute': '609-new-billing-module'
  }, "Calculate allocation"))))));
});
export default BillDetailsForm;